// React i hooks
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

// API servisi i tipovi
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { getUsersAPI } from "services/api/users/api";
import { UserRow, UserSearchFormValues } from "services/api/users/types";

// Navigacija
import { Routes } from "../../navigation";
import { Routes as UserRoutes } from "ui/modules/users/navigation";

// Hook-ovi
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

// Tipovi
import { UserSearchPageCmp, UserSearchPageProps } from "./types";

// UI komponente
import { Breadcrumb } from "ui/components/Breadcrumb";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { Pagination } from "services/search";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import { Button } from "ui/components/Button/Button";
// Lokalne komponente
import UserSearchFilters from "./components/UserSearchFilters/UserSearchFilters";
import UserSearchResults from "./components/UserSearchResults/UserSearchResults";
import UserEditCard from "../UserDetailPage/components/UserDetailCard/components/UserEditCard/UserEditCard";

// Konstante
import {
  USER_SEARCH_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// Helpers
import { transformRoute, useTranslate } from "helpers";

const UserSearchPage: UserSearchPageCmp = (props: UserSearchPageProps) => {
  const [searchResults, setSearchResults] = useState<
    DataWithPagination<UserRow>
  >(initialDataWithPagination);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAddNewModal, setShowAddNewModal] = useState<boolean>(false);

  const navigate = useNavigate();
  const { t } = useTranslate();

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    name?: string | null,
    email?: string | null,
    phone?: string | null,
    address?: string | null,
    roleId?: string | null
  ) => {
    setIsLoading(true);
    try {
      const results = await getUsersAPI(
        pagination,
        name,
        email,
        phone,
        address,
        roleId
      );

      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleRowClick = useCallback(async (id: string) => {
    navigate(
      transformRoute(UserRoutes.UserDetailPageRouter, {
        id,
      })
    );
  }, []);

  const handleAddNew = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setUserId(null);
      setShowAddNewModal(true);
    },
    []
  );

  const handleSearchChange = useCallback(
    (searchFormValues: UserSearchFormValues) => {
      fetchSearchResult(
        searchResults.pagination,
        searchFormValues.search,
        searchFormValues.email,
        searchFormValues.phone,
        searchFormValues.address,
        searchFormValues.roleId
      );
    },
    []
  );

  const handleEmailClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>, emailAddress: string) => {
      event.stopPropagation();
      event.preventDefault();
      window.location.href = `mailto:${emailAddress}`;
    },
    []
  );

  const handlePhoneClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>, phone: string) => {
      event.stopPropagation();
      event.preventDefault();
      window.location.href = `tel:${phone}`;
    },
    []
  );

  const handleGoogleMapClick = useCallback(
    (event: React.MouseEvent<HTMLSpanElement>, address: string) => {
      event.stopPropagation();
      event.preventDefault();
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address
      )}`;
      window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
    },
    []
  );

  const handleEditSuccess = useCallback(() => {
    fetchSearchResult(searchResults.pagination);
    setUserId(null);
    setShowAddNewModal(false);
  }, []);

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination);
  };

  return (
    <>
      <div className="">
        <div className="bg-white rounded-t-md border border-b mb-4">
          <PageHeader>
            <div className="flex items-center justify-between">
              <div>
                <PageTitle
                  title={USER_SEARCH_PAGE_TITLE.text}
                  icon={USER_SEARCH_PAGE_TITLE.icon}
                  iconColor={USER_SEARCH_PAGE_TITLE.iconColor}
                />
                <Breadcrumb />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="primary"
                  children={t("admin.user.search.add")}
                  startIcon="/media/icons/plus.svg"
                  onClick={handleAddNew}
                />
              </div>
            </div>
          </PageHeader>
        </div>
        <div className="px-4">
          <UserSearchFilters
            onSearchChange={handleSearchChange}
          />
          {/* Dok se dohvaća, rezultati se zamute i preko njih ide spinner — relative je
              nužan jer overlay prekriva roditelja. */}
          <div className="relative">
            {!isLoading && searchResults.pagination.totalPages === 0 ? (
              <NoResults message={t("user.search.noresults")} />
            ) : (
              <UserSearchResults
                searchResults={searchResults}
                selectedRows={selectedRows}
                isLoading={isLoading}
                onRowClick={handleRowClick}
                onPaginationChange={handlePaginationChange}
                onSelectedChange={handleSelectedChange}
                onEmailClick={handleEmailClick}
                onPhoneClick={handlePhoneClick}
                onGoogleMapClick={handleGoogleMapClick}
              />
            )}
            <Loader isLoading={isLoading} overlay />
          </div>
        </div>
      </div>

      <UserEditCard
        userId={userId}
        show={showAddNewModal}
        onSuccess={handleEditSuccess}
        onClose={() => setShowAddNewModal(false)}
      />

    </>
  );
};

UserSearchPage.route = Routes.UserSearchPageRouter;

export default UserSearchPage;
